import {
    collection,
    doc,
    setDoc,
    getDoc,
    deleteDoc,
    getDocs,
    orderBy,
    query,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import type { FavouriteRefDoc, PlaceId } from '../types';
import { assertKeysOnly } from '../src/dev/tosGuards';
import { safeErrorMessage } from '../utils/sanitize';

/**
 * Favourites Service - ID-Only Schema
 *
 * Favourites are stored as subcollections under the user:
 * favourites/{userId}/items/{placeId} = { placeId, savedAt }
 *
 * We no longer persist Google Places attributes (name, photos, rating, etc.)
 * to align with Google Maps Platform ToS.
 * UI hydrates display fields from the Firestore `display` collection
 * (batchGetGymsFromDisplay) at runtime.
 */

/**
 * Get the favourites subcollection reference for a user
 */
const getUserFavouritesRef = (userId: string) => {
    return collection(db, 'favourites', userId, 'items');
};


/**
 * Add an item to user's favourites.
 * Writes ID-only schema: { placeId, savedAt }
 * @param gymId  The stable ID (or Google Place ID) of the item to favourite
 * @param type   The item type — currently always 'gym'; accepts future types
 */
export const addFavourite = async (userId: string, gymId: PlaceId, _type: 'gym' = 'gym'): Promise<void> => {
    try {
        const favouriteRef = doc(db, 'favourites', userId, 'items', gymId);

        const data = {
            placeId: gymId,
            savedAt: serverTimestamp()
        };

        assertKeysOnly("favourites payload", data, ['placeId', 'savedAt']);

        await setDoc(favouriteRef, data, { merge: true });
    } catch (error) {
        console.error(`[favouritesService] Failed to add favourite:`, safeErrorMessage(error));
        throw new Error('Failed to save to favourites. Please try again.');
    }
};

/**
 * Remove a gym from user's favourites.
 * Identifier-agnostic: deletes doc for any provided ID.
 */
export const removeFavourite = async (userId: string, gymId: PlaceId): Promise<void> => {
    try {
        // We delete the specific ID provided.
        // If the frontend has the other ID (e.g. Place ID vs Stable ID), 
        // it should have passed the correct one from the record it loaded.
        const favouriteRef = doc(db, 'favourites', userId, 'items', gymId);
        await deleteDoc(favouriteRef);
    } catch (error) {
        console.error('[favouritesService] Failed to remove favourite:', safeErrorMessage(error));
        throw new Error('Failed to remove from favourites. Please try again.');
    }
};

/**
 * Get all favourites for a user.
 * Returns FavouriteRefDoc[] (placeId + savedAt only).
 */
export const getFavourites = async (userId: string): Promise<FavouriteRefDoc[]> => {
    const favouritesRef = getUserFavouritesRef(userId);
    const q = query(favouritesRef, orderBy('savedAt', 'desc'));

    const snapshot = await getDocs(q);
    const results: FavouriteRefDoc[] = [];

    for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const placeId = docSnap.id;

        if (!placeId) continue;

        results.push({
            placeId,
            savedAt: data.savedAt
        });
    }

    return results;
};

/**
 * Check if a gym is in user's favourites.
 * Handles dual-ID lookup (Stable ID or Google Place ID).
 */
export const isFavourite = async (userId: string, gymId: PlaceId, googlePlaceId?: string): Promise<boolean> => {
    if (!gymId) return false;

    // 1. Try provided gymId (could be stableId or PlaceId)
    const ref1 = doc(db, 'favourites', userId, 'items', gymId);
    const snap1 = await getDoc(ref1);
    if (snap1.exists()) return true;

    // 2. Try googlePlaceId if provided and different
    if (googlePlaceId && googlePlaceId !== gymId) {
        const ref2 = doc(db, 'favourites', userId, 'items', googlePlaceId);
        const snap2 = await getDoc(ref2);
        return snap2.exists();
    }

    return false;
};

/**
 * Get favourite IDs for a user (for quick lookups).
 * Returns Set of placeIds.
 */
export const getFavouriteIds = async (userId: string): Promise<Set<PlaceId>> => {
    const favouritesRef = getUserFavouritesRef(userId);
    const snapshot = await getDocs(favouritesRef);
    const ids = new Set<PlaceId>();

    snapshot.docs.forEach((docSnap) => {
        const placeId = docSnap.id;
        if (placeId) {
            ids.add(placeId);
        }
    });

    return ids;
};
